import {
  Box,
  FieldLabel,
  FieldSwitch,
  FormField,
  FormGrid,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { ChangeEvent, useCallback, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { useIsChanged } from 'components/powerFlowShared/hooks/useIsChanged';
import { AnnouncementPicker } from 'components/shared/announcementPicker';
import { AdapterTypes } from 'components/shared/announcementPicker/adapters';
import { AnnouncementItem } from 'components/shared/announcementPicker/adapters/announcementAdapter';
import { FlowManagerEditModalComponentT } from 'fable/flowEditor';

import { FlowCallNodeAudioMessage } from '../nodeTypes';

import styles from './audioMessage.module.scss';

const ANNOUNCEMENT_FIELD = 'announcement';

type Props = FlowManagerEditModalComponentT<FlowCallNodeAudioMessage>;

type Errors = {
  [Key in typeof ANNOUNCEMENT_FIELD]?: string;
};

export function AudioMessage({
  node,
  nodeLabel,
  triggerClose,
  replaceNode,
}: Props): JSX.Element {
  const { payload } = node;
  const {
    announcement: { file: savedAnnouncement = null } = {},
    hangUpAfterAnnouncement: savedIsChecked,
  } = payload || {};
  const [isHangChecked, setIsHangChecked] = useState(savedIsChecked);
  const [announcement, setAnnouncement] = useState<AnnouncementItem | null>(
    savedAnnouncement,
  );
  const [errors, setErrors] = useState<Errors>({});
  const isChanged = useIsChanged(
    [savedIsChecked, isHangChecked],
    [savedAnnouncement, announcement],
  );

  const validate = useCallback(() => {
    const errors: Errors = {};

    if (!announcement) {
      errors[ANNOUNCEMENT_FIELD] = 'Please select an announcement file';
    }

    setErrors(errors);

    return Object.keys(errors).length === 0;
  }, [announcement, setErrors]);

  const handleSave = useCallback(() => {
    const isValid = validate();

    if (!isValid) {
      return;
    }

    const { payload: existingNodePayload } = node;
    const payload: FlowCallNodeAudioMessage = {
      ...node,
      icon: 'i-announcement',
      isAction: false,
      isEditable: true,
      isRemovable: true,
      nodeType: FlowNodeTypes.AUDIO_MESSAGE,
      overlay: [],
      payload: {
        announcement: {
          file: announcement ?? null,
        },
        ...(isHangChecked
          ? {
              hangUpAfterAnnouncement: true,
              afterAnnouncementNodeId: null,
            }
          : {
              hangUpAfterAnnouncement: false,
              afterAnnouncementNodeId:
                existingNodePayload?.afterAnnouncementNodeId || uuid(),
            }),
      },
      text: 'Announcement',
    };

    replaceNode(payload);
    triggerClose();
  }, [isHangChecked, announcement, node, validate, triggerClose, replaceNode]);
  const onAnnouncementSelect = useCallback(
    (item: AnnouncementItem) => {
      setAnnouncement(item);
    },
    [setAnnouncement],
  );
  const onHangChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const {
        target: { checked: isChecked },
      } = event;

      setIsHangChecked(isChecked);
    },
    [setIsHangChecked],
  );

  const announcementError = errors[ANNOUNCEMENT_FIELD];

  return (
    <Box width="600px">
      <ModalHead title={nodeLabel} onClose={triggerClose} />
      <ModalBody>
        <Text.Paragraph subdued>
          Record or upload an audio message that’ll play when your customers
          call you (e.g. informing them when you’re closed for the holidays,
          that the phone call is going to be recorded, etc.).
        </Text.Paragraph>
        <FormGrid className={styles.form}>
          <FormField>
            <AnnouncementPicker
              adapter={AdapterTypes.Announcement}
              selectedAudio={announcement}
              onSelect={onAnnouncementSelect}
              fieldProps={{ meta: { error: announcementError } }}
            />
          </FormField>
          <FormField>
            <FieldLabel className={styles.label}>
              After audio message
            </FieldLabel>
            <div className={styles.optionWithSwitch}>
              <span>Hang up after audio message</span>
              <FieldSwitch
                input={{
                  name: 'announcement-switch',
                  value: 'announcement-switch',
                  onChange: onHangChange,
                  checked: isHangChecked,
                }}
              />
            </div>
          </FormField>
        </FormGrid>
      </ModalBody>
      <ModalFoot
        confirmOnClick={handleSave}
        confirmName={getConfirmButtonVerb(node)}
        confirmProps={{ disabled: !isChanged }}
        cancelOnClick={triggerClose}
      />
    </Box>
  );
}
